<template>
    <!-- 에이전트 쓰기 승인 방식 — GET/POST /api/approval-mode (seller-profile-store approvalRoutes).
         서버는 로그인한 쿠플러스 계정(세션 회사) 것만 읽고 바꾼다 → 지금 쓰는 계정 줄에서만 바꿀 수 있다.
         바꾸기는 이 화면(브라우저 같은 출처)에서만 된다. 에이전트(MCP)는 못 바꾼다. -->
    <section class="apr-mode">
        <h6 class="apr-mode__title">에이전트 쓰기 승인 방식</h6>
        <p class="apr-mode__sub">
            AI 에이전트가 쿠팡(로켓그로스·판매자배송 Open API)에 쓰기 전에 사람 승인을 받을지 정해요.
            기본은 «매번 승인»이에요. 이 설정은 이 화면에서 사람만 바꿀 수 있어요.
        </p>
        <p v-if="!current" class="apr-mode__note">이 계정으로 전환한 뒤 바꿀 수 있어요 (승인 방식은 로그인한 계정 기준이에요).</p>
        <template v-else>
            <p v-if="loading" class="apr-mode__note">불러오는 중이에요…</p>
            <template v-else-if="approval">
                <div class="apr-mode__radios" role="radiogroup" aria-label="승인 방식">
                    <label class="apr-mode__radio">
                        <input type="radio" value="always" v-model="mode" :disabled="busy"> 매번 승인 <span class="apr-mode__hint">(모든 쓰기를 사람이 승인)</span>
                    </label>
                    <label class="apr-mode__radio">
                        <input type="radio" value="limit" v-model="mode" :disabled="busy"> 한도 안이면 자동 <span class="apr-mode__hint">(가격·재고 변경만, 정한 한도 안일 때)</span>
                    </label>
                    <label v-if="mode === 'byTool' || byToolCount" class="apr-mode__radio">
                        <input type="radio" value="byTool" v-model="mode" :disabled="busy"> 도구별 <span class="apr-mode__hint">(자동 {{ byToolAuto }}개 · 저장된 도구별 설정 유지)</span>
                    </label>
                </div>
                <div v-if="mode === 'limit'" class="apr-mode__limits">
                    <label class="apr-mode__label" for="aprPricePct">가격 변경 한도 (%) · 0~50</label>
                    <input id="aprPricePct" type="number" min="0" max="50" step="0.5" class="form-control form-control-sm" v-model="pricePct" :disabled="busy" placeholder="비우면 가격 변경은 매번 승인">
                    <label class="apr-mode__label" for="aprStockQty">재고 변경 한도 (개) · 0~10000</label>
                    <input id="aprStockQty" type="number" min="0" max="10000" step="1" class="form-control form-control-sm" v-model="stockDeltaQty" :disabled="busy" placeholder="비우면 재고 변경은 매번 승인">
                </div>
                <!-- 서버 FIXED_ASK_TOOLS(GET 응답 fixedAsk)로 그린다 — 서버 목록이 바뀌면 화면도 따라간다 -->
                <div v-if="fixedAsk.length" class="apr-mode__fixed">
                    설정과 무관하게 항상 사람 승인:
                    <ul class="apr-mode__fixed-list">
                        <li v-for="t in fixedAsk" :key="t">{{ toolLabel(t) }}</li>
                    </ul>
                </div>
                <div class="apr-mode__actions">
                    <b-button size="sm" variant="primary" :disabled="busy || !dirty" @click="save">승인 방식 저장</b-button>
                </div>
            </template>
            <p v-if="message" class="apr-mode__msg" :class="{ 'apr-mode__msg--bad': messageBad }" role="status">{{ message }}</p>
        </template>
    </section>
</template>

<script>
import axios from 'axios';

// 도구 이름 → 읽는 말. 목록 자체는 서버(fixedAsk)가 정하고, 모르는 도구는 이름 그대로 보인다
const TOOL_LABEL = {
    'marketplace.operation.deleteSellerProduct': '상품 삭제',
    'marketplace.sourcing.requestProductApproval': '판매 시작(판매 승인 요청)',
    'marketplace.sourcing.registerProduct': '상품 등록',
    'marketplace.sourcing.updateProduct': '상품 전체 수정(판매 승인 재요청)',
    'marketplace.operation.setItemSaleStatus': '판매 중지·재개',
};
const numOrNull = v => (v === '' || v == null || !isFinite(Number(v)) ? null : Number(v));

export default {
    name: 'ApprovalModeSection',
    props: {
        // 지금 로그인해 쓰는 계정 줄인가 — 서버가 세션 회사 것만 읽고 바꾸므로 아니면 보기만 막는다
        current: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return { loading: false, busy: false, approval: null, fixedAsk: [], mode: 'always', pricePct: '', stockDeltaQty: '', message: '', messageBad: false };
    },
    computed: {
        byToolCount() {
            return this.approval ? Object.keys(this.approval.byTool || {}).length : 0;
        },
        byToolAuto() {
            const b = (this.approval && this.approval.byTool) || {};
            return Object.keys(b).filter(k => b[k] === 'auto').length;
        },
        dirty() {
            if (!this.approval) return false;
            const lim = this.approval.limit || {};
            return this.mode !== this.approval.mode
                || numOrNull(this.pricePct) !== (lim.pricePct == null ? null : lim.pricePct)
                || numOrNull(this.stockDeltaQty) !== (lim.stockDeltaQty == null ? null : lim.stockDeltaQty);
        },
    },
    watch: {
        current: {
            immediate: true,
            handler(v) { if (v) this.load(); },
        },
    },
    methods: {
        toolLabel(t) {
            return TOOL_LABEL[t] || t;
        },
        apply(a) {
            this.approval = a;
            this.mode = a.mode || 'always';
            const lim = a.limit || {};
            this.pricePct = lim.pricePct == null ? '' : lim.pricePct;
            this.stockDeltaQty = lim.stockDeltaQty == null ? '' : lim.stockDeltaQty;
        },
        async load() {
            this.loading = true;
            this.message = '';
            try {
                const res = await axios.get('/api/approval-mode');
                const d = res.data || {};
                if (!d.ok || !d.approval) { this.message = d.message || '승인 방식을 읽지 못했어요.'; this.messageBad = true; return; }
                this.fixedAsk = Array.isArray(d.fixedAsk) ? d.fixedAsk : [];
                this.apply(d.approval);
            } catch (e) {
                console.error('[ApprovalModeSection] 승인 방식 읽기 실패', e);
                const d = e && e.response && e.response.data;
                this.message = (d && d.message) || '승인 방식을 읽지 못했어요.';
                this.messageBad = true;
            } finally {
                this.loading = false;
            }
        },
        async save() {
            const limit = {};
            const p = numOrNull(this.pricePct);
            const q = numOrNull(this.stockDeltaQty);
            if (p !== null) limit.pricePct = p;
            if (q !== null) limit.stockDeltaQty = Math.round(q);
            if (this.mode === 'limit' && !Object.keys(limit).length) {
                this.message = '«한도 안이면 자동»은 가격 또는 재고 한도를 하나 이상 넣어야 해요.';
                this.messageBad = true;
                return;
            }
            const label = this.mode === 'always' ? '매번 승인' : this.mode === 'limit' ? '한도 안이면 자동' : '도구별';
            if (!confirm(`에이전트 쓰기 승인 방식을 «${label}»(으)로 바꿀까요?`)) return;
            this.busy = true;
            this.message = '';
            try {
                // 도구별 설정·1688 보내기 한도는 이 화면에서 안 바꾼다 — 저장된 값을 그대로 실어 지우지 않는다
                const approval = { mode: this.mode, byTool: this.approval.byTool || {}, limit, sendLimits: this.approval.sendLimits || {} };
                const res = await axios.post('/api/approval-mode', { approval });
                const d = res.data || {};
                if (!d.ok) { this.message = d.message || '저장하지 못했어요.'; this.messageBad = true; return; }
                this.apply(d.approval || approval);
                this.message = '저장했어요.';
                this.messageBad = false;
            } catch (e) {
                console.error('[ApprovalModeSection] 승인 방식 저장 실패', e);
                const d = e && e.response && e.response.data;
                this.message = (d && d.message) || '저장하지 못했어요.';
                this.messageBad = true;
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.apr-mode {
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #e9ecef;
    &__title { font-size: 13px; font-weight: 700; margin-bottom: 4px; }
    &__sub { font-size: 12px; color: #6c757d; margin-bottom: 8px; }
    &__note { font-size: 12px; color: #6c757d; margin-bottom: 4px; }
    &__radios { display: flex; flex-direction: column; gap: 4px; }
    &__radio { font-size: 13px; margin: 0; cursor: pointer; }
    &__hint { font-size: 11.5px; color: #868e96; }
    &__limits { margin-top: 6px; }
    &__label { font-size: 12px; margin: 6px 0 2px; display: block; }
    &__fixed { font-size: 11.5px; color: #868e96; margin: 8px 0 0; }
    &__fixed-list { margin: 2px 0 0; padding-left: 18px; }
    &__actions { display: flex; justify-content: flex-end; margin-top: 8px; }
    &__msg { font-size: 12px; margin-top: 6px; }
    &__msg--bad { color: #c82333; }
}
</style>
